<template>
  <DetailInfo class="interview-info-container" :btnList="btnList">
    <div class="interview-content">
      <!-- 面试管理 -->
      <CollapseContent
        class="management"
        :title="$t(`${i18nPrefix}.title.interviewManagement`)"
      >
        <InterviewManagement
          :listData="searchResult"
          :loading="interviewListLoading"
          :isDisabled="isDisabled"
          @search="searchInterviewRecords"
          @edit="handleEditInterview"
          @cancel="handleCancelInterview"
          @resend="handleResendInterview"
          @sendNew="handleSendNewInterview"
          @sendRating="handleSendRatingInterview"
          @confirmInterview="handleConfirmInterview"
          @viewGroupInterview="handleViewGroupInterview"
        />
      </CollapseContent>

      <!-- 面试评估总评 -->
      <div v-if="hasInterviewOverallRating" v-rect-loading="interviewResultLoading" class="interview-overall-rating" :class="interviewOverallRatingCollapsed && 'is-collapsed'">
        <div class="overall-rating-header">
          <div class="overall-rating-left">
            <button class="overall-rating-title-row" type="button" @click="toggleInterviewOverallRating">
              <span class="overall-rating-title">{{ $t(`${i18nPrefix}.title.interviewOverallRating`) }}</span>
              <i class="el-icon-arrow-down overall-rating-arrow" :class="interviewOverallRatingCollapsed && 'is-collapsed'"></i>
            </button>
            <div v-if="activeInterviewOverallRating && !interviewOverallRatingCollapsed" class="overall-rating-control">
              <div class="overall-rating-tabs">
                <div
                  v-for="(item, index) in interviewOverallRatingList"
                  :key="item.id"
                  class="overall-rating-tab"
                  :class="index === activeInterviewOverallRatingIndex && 'is-active'"
                  @click="handleInterviewOverallRatingTabClick(index)"
                >
                  <span>{{ item.label }}</span>
                  <span class="overall-rating-tab-score">
                    <RecommendationResultChart
                      class="overall-rating-tab-score-value"
                      ringBackgroundColor="#E4E7ED"
                      :decision="item.decision"
                      :score="item.score"
                      :completion="item.completion"
                      :hmInfoList="item.hmInfoList"
                      :fitScoreStyle="overallRatingTabScoreStyle"
                      :chartOption="overallRatingTabScoreChartOption"
                      :circleWidth="2"
                      :fontSize="14"
                    />
                  </span>
                </div>
              </div>
              <div class="overall-rating-time">{{ activeInterviewOverallRatingTime }}</div>
            </div>
          </div>
          <div class="overall-rating-score">
            <RecommendationResultChart
              class="overall-score-circle"
              ringBackgroundColor="#E4E7ED"
              :decision="activeInterviewOverallRating.decision"
              :score="activeInterviewOverallRating.score"
              :completion="activeInterviewOverallRating.completion"
              :hmInfoList="activeInterviewOverallRating.hmInfoList"
              :fitScoreStyle="overallScoreStyle"
              :chartOption="overallScoreChartOption"
              :circleWidth="4"
              :fontSize="30"
            />
            <div class="overall-score-label">{{ $t(`${i18nPrefix}.label.overall`) }}</div>
          </div>
        </div>
        <div v-show="!interviewOverallRatingCollapsed" class="overall-rating-detail">
          <HmInterviewEvaluation
            v-if="activeInterviewOverallRating.type === INTERVIEW_OVERALL_RATING_TYPE.NEW"
            class="hm-interview-evaluation-section"
            result-type="interview"
            :evaluation="activeInterviewOverallRating.evaluation"
          />
          <RatingWrapper
            v-else
            :ratingList="activeInterviewOverallRating.ratingList"
          />
        </div>
      </div>

      <!-- 面试评估表 -->
      <CollapseContent
        v-if="ratingQuestions.length > 0"
        class="interview-rating-form"
        :title="$t(`${i18nPrefix}.title.interviewRatingForm`)"
      >
        <QuestionWrapper
          hide-answer
          :questionnaireResultList="ratingQuestions"
        />
      </CollapseContent>

      <!-- AI 面试 -->
      <CollapseContent
        v-if="aiInterviewRecord && aiInterviewRecord.finished_time && aiInterviewRecord.status === AI_INTERVIEW_STATUS.FINISHED"
        class="ai-interview-form"
        :title="$t(`${i18nPrefix}.title.aiInterview`)"
      >
        <AIInterview
          class="info-item"
          isCustomScore
          isCustomReportUrl
          isCustomQuestion
          hiddenIcon
          hiddenTitle
          :candidateBaseInfo="candidateBaseInfo"
          :questionList="aiInterviewRecord.question_list"
          :totalScore="aiInterviewRecord.total_score"
          :pdfUrl="aiInterviewRecord.report_url"
          :finishedTime="aiInterviewRecord.finished_time"
        >
          <template #score="{totalScore}">
            <div class="ai-score value">{{ [null, undefined].includes(totalScore) ? '-' : totalScore }}</div>
          </template>
          <template #report="{pdfUrl}">
            <div class="value" :class="pdfUrl && 'link'" @click="downloadReport(pdfUrl)">{{ (pdfUrl && $t(`${i18nPrefix}.label.clickToView`)) || '-' }}</div>
          </template>
          <template #question="{questionList}">
            <div v-for="(question, index) in questionList" class="question-info info-line" :key="index" :class="question.answer || 'no-answer'">
              <div class="question line">
                <div class="label">{{ $t(`${i18nPrefix}.label.aiQuestions`) }}</div>
                <div class="value">{{ question.title }}</div>
              </div>
              <div class="question-answer">
                <div class="label" :class="[isEn()&&'en-answer']">{{ $t(`${i18nPrefix}.label.answers`) }}</div>
                <div v-if="question.question_type === AI_INTERVIEW_QUESTION_TYPE.VIDEO || question.question_type === AI_INTERVIEW_QUESTION_TYPE.HAND_DETECTION || question.question_type === AI_INTERVIEW_QUESTION_TYPE.HAND_DETECTION_V2">
                  <div class="answer video">
                    <div class="value video-container" @click="onPlayerContainerClick(question)">
                      <img v-if="question.answer" class="video-play-icon" :src="$DjangoStaticUrl('image/video/icon_video.png')"/>
                      <div v-else class="upload-btn">-</div>
                    </div>
                  </div>
                </div>
                <div v-else-if="question.question_type === AI_INTERVIEW_QUESTION_TYPE.AUDIO" class="answer">
                  <div class="answer audio">
                    <div class="value audio-container" @click="onPlayerContainerClick(question)">
                      <img v-if="question.answer" class="audio-play-icon" :src="$DjangoStaticUrl('image/video/icon_video.png')"/>
                      <div v-else class="upload-btn">-</div>
                    </div>
                  </div>
                </div>
                <div v-else-if="question.question_type === AI_INTERVIEW_QUESTION_TYPE.SINGLE" class="answer">{{ question.answer }}</div>
                <div v-else-if="question.question_type === AI_INTERVIEW_QUESTION_TYPE.MULTI" class="answer">{{ question.answer }}</div>
                <div v-else-if="question.question_type === AI_INTERVIEW_QUESTION_TYPE.SINGLE_LINE" class="answer">{{ question.answer }}</div>
                <div v-else-if="question.question_type === AI_INTERVIEW_QUESTION_TYPE.MULTI_LINE" class="answer">{{ question.answer }}</div>
                <div v-else-if="question.question_type === AI_INTERVIEW_QUESTION_TYPE.GAME" class="answer">{{ question.answer }}</div>
                <div v-else-if="question.question_type === AI_INTERVIEW_QUESTION_TYPE.NUMBER" class="answer">{{ question.answer }}</div>
                <div v-else-if="question.question_type === AI_INTERVIEW_QUESTION_TYPE.LOCATION_MAP" class="answer">{{ question.answer }}</div>
                <div v-else-if="question.question_type === AI_INTERVIEW_QUESTION_TYPE.DATE" class="answer">{{ question.answer }}</div>
                <div v-else-if="question.question_type === AI_INTERVIEW_QUESTION_TYPE.REGION" class="answer">{{ question.answer }}</div>
                <div v-else-if="question.question_type === AI_INTERVIEW_QUESTION_TYPE.SORT" class="answer">{{ question.answer }}</div>
                <div v-else-if="question.question_type === AI_INTERVIEW_QUESTION_TYPE.FILE_UPLOAD" class="answer">{{ question.answer }}</div>
                <div v-else-if="question.question_type === AI_INTERVIEW_QUESTION_TYPE.ROLL_AXIS" class="answer">{{ question.answer }}</div>
                <div v-else-if="question.question_type === AI_INTERVIEW_QUESTION_TYPE.MOVEMENT_DETECTION" class="answer">{{ question.answer }}</div>
                <div v-else-if="question.question_type === AI_INTERVIEW_QUESTION_TYPE.IMAGE_QUALITY" class="answer">{{ question.answer }}</div>
                <div v-else-if="question.question_type === AI_INTERVIEW_QUESTION_TYPE.SIGN" class="answer">{{ question.answer }}</div>
                <div v-else-if="question.question_type === AI_INTERVIEW_QUESTION_TYPE.AI_MODEL" class="answer">{{ question.answer }}</div>
                <div v-else-if="question.question_type === AI_INTERVIEW_QUESTION_TYPE.GLOBAL_INTENTION_STORE" class="answer">{{ question.answer }}</div>
                <div v-else-if="question.question_type === AI_INTERVIEW_QUESTION_TYPE.HEIGHT_DETECTION_V2" class="answer">{{ question.answer }}</div>
                <div v-else-if="question.question_type === AI_INTERVIEW_QUESTION_TYPE.PPT" class="answer">{{ question.answer }}</div>
                <div v-else-if="question.question_type === AI_INTERVIEW_QUESTION_TYPE.FIND_TRICK" class="answer">{{ question.answer }}</div>
                <div v-else-if="question.question_type === AI_INTERVIEW_QUESTION_TYPE.CONVERSATION" class="answer">{{ question.answer }}</div>
                <div v-else class="answer">-</div>
              </div>
            </div>
          </template>
        </AIInterview>
      </CollapseContent>
    </div>
    <InviteInterview
      ref="inviteDialog"
      :applicationList="inviteDialog.applicationList"
      :saveWithEvent="inviteDialog.saveWithEvent"
      :isEditingGroupInterview="isEditingGroupInterview"
      @sent="handleSentInterviewInvite"
      @save="handleSaveInterviewInvite"
    />
    <DialogWrapper
      class="confirm-create-job-interview-room"
      closeOnCancel
      :showClose="false"
      :close-on-click-modal="false"
      :title="$t(`${i18nPrefix}.message.jobAIInterviewRoomError`)"
      :show.sync="createJobInterviewRoomConfirmDialog.show"
      :loading="createJobInterviewRoomConfirmDialog.loading"
    >
      <span class="content-text">{{ $t(`${i18nPrefix}.message.jobAIInterviewRoomErrorContent`) }}</span>
      <template #footer>
        <div class="dialog-footer">
          <el-button class="footer-btn cancel-btn" @click="handleCancelCreateJobAIInterviewRoom">{{ $t('common.btn.cancel') }}</el-button>
          <el-button class="footer-btn save-btn" @click="handleConfirmCreateJobAIInterviewRoom">{{ $t('common.btn.select') }}</el-button>
        </div>
      </template>
    </DialogWrapper>
    <DialogWrapper
      hideFooter
      class="player-dialog"
      :title="popInfo.title"
      :show.sync="popInfo.show"
      @close="closePlayerDialog"
    >
      <div v-if="popInfo.type === 'video'" class="video-player-container">
        <video
          v-if="popInfo.show && popInfo.url"
          controls
          autoplay
          ref="videoPlayer"
          class="video"
          width="375"
          :src="popInfo.url"
        ></video>
      </div>
      <div v-else class="audio-player-container">
        <audio
          v-if="popInfo.show && popInfo.url"
          controls
          autoplay
          ref="audioPlayer"
          class="audio"
          :src="popInfo.url"
        ></audio>
      </div>
    </DialogWrapper>
    <InterviewConfirmDialog
      ref="interviewConfirmDialog"
      :applicationList="applicationList"
      @success="handleTeamsUrlCreateSuccess"
    ></InterviewConfirmDialog>
    <QuestionnaireSelectorDialog
      ref="questionnaireSelectorDialog"
      :show.sync="showQuestionnaireSelector"
      :selectedQuestionnaire="selectedQuestionnaire"
      @confirm="handleConfirmQuestionnaire"
    />
  </DetailInfo>
</template>

<script>
import InterviewConfirmDialog from '@/views/adminNew/applicantList/dialog/interviewConfirmDialog.vue'
import DetailInfo from './components/detailInfo.vue'
import InterviewManagement from './components/interviewManagement.vue'
import RatingWrapper from './components/ratingsWrapper.vue'
import CollapseContent from './components/collapseContent.vue'
import QuestionWrapper from './components/questionWrapper.vue'
import HmInterviewEvaluation from './components/hmInterviewEvaluation.vue'
import InviteInterview from '../../applicantList/dialog/inviteInterview.vue'
import questionnaireResultsMixin from './mixins/questionnaireResultsMixin'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import AIInterview from './components/aiInterview.vue'
import { mapActions } from 'vuex'
import { addI18nProperty, isEn } from '@/i18n'
import _ from 'lodash'
import moment from 'moment'
import { INTERVIEW_STATUS, AI_INTERVIEW_STATUS, AI_INTERVIEW_QUESTION_TYPE, INTERVIEW_OPERATION_TYPE, INTERVIEW_RATING_DECISION, RATE_STATUS_MAP } from '@/constant/interview'
import { openNewBrowserWindowByUrl, addQueryInPath } from '@/utils/common'
import QuestionnaireSelectorDialog from '@/views/adminNew/applicantList/dialog/components/questionnaireSelectorDialog.vue'
import RecommendationResultChart from '@/views/adminNew/components/recommendationResultChart.vue'
import { ROOT_COMPANY_ID } from '@/constant/company.js'

const INVITE_DIALOG_MODE = {
  CREATE: 'CREATE',
  EDIT: 'EDIT',
  RESEND: 'RESEND'
}

const INTERVIEW_OVERALL_RATING_TYPE = {
  NEW: 'new',
  LEGACY: 'legacy'
}

export default {
  mixins: [questionnaireResultsMixin],
  components: {
    QuestionnaireSelectorDialog,
    InterviewConfirmDialog,
    DetailInfo,
    InterviewManagement,
    RatingWrapper,
    CollapseContent,
    QuestionWrapper,
    HmInterviewEvaluation,
    InviteInterview,
    DialogWrapper,
    AIInterview,
    RecommendationResultChart
  },
  props: {
    candidateBaseInfo: {
      required: true
    },
    isDisabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isEn,
      i18nPrefix: 'admin.application.candidate',
      AI_INTERVIEW_STATUS,
      AI_INTERVIEW_QUESTION_TYPE,
      inviteDialog: {
        mode: INVITE_DIALOG_MODE.CREATE,
        saveWithEvent: false,
        originInvitation: null,
        invitation: null,
        applicationList: []
      },
      interviewListLoading: false,
      interviewList: [],
      searchResult: [],
      hasMicrosoftGraphAuthToken: false,
      microsoftGraphAuthUrl: null,
      microsoftGraphEventId: null,
      createJobInterviewRoomConfirmDialog: {
        show: false,
        loading: false
      },
      aiInterviewRecord: null,
      aiInterviewReminderLoading: false,
      popInfo: {
        show: false,
        type: null,
        title: null,
        url: null
      },
      showQuestionnaireSelector: false,
      selectedQuestionnaire: null,
      INTERVIEW_OVERALL_RATING_TYPE,
      hmInterviewQuestionnaireResults: null,
      activeInterviewOverallRatingIndex: 0,
      interviewOverallRatingCollapsed: false,
      overallScoreStyle: {
        width: '70px',
        height: '70px'
      },
      overallScoreChartOption: {
        series: {
          data: [{
            title: {
              fontWeight: 500,
              offsetCenter: [0, '10%']
            }
          }]
        }
      },
      overallRatingTabScoreStyle: {
        width: '32px',
        height: '32px'
      },
      overallRatingTabScoreChartOption: {
        series: {
          data: [{
            title: {
              fontWeight: 500
            }
          }]
        }
      },
      interviewResultLoading: false
    }
  },
  computed: {
    legacyInterviewRateList () {
      return _.filter(this.candidateBaseInfo.interview_rate_list, item => item && item.source_type === 'legacy')
    },
    hasLegacyInterviewOverallRating () {
      return this.legacyInterviewRateList.length > 0
    },
    hasNewInterviewOverallRating () {
      return Boolean(this.hmInterviewQuestionnaireResults && this.hmInterviewQuestionnaireResults.result_list.length)
    },
    hasInterviewOverallRating () {
      return this.interviewOverallRatingList.length > 0
    },
    interviewOverallRatingList () {
      const list = []
      if (this.hasNewInterviewOverallRating) {
        const questionnaireOverallScoreMap = _.get(this.hmInterviewQuestionnaireResults, 'overall_total_score.questionnaire')
        const questionnaireOverallCompletionMap = _.get(this.hmInterviewQuestionnaireResults, 'overall_completion_rate')
        const questionnaireOverallDecisionMap = _.get(this.hmInterviewQuestionnaireResults, 'overall_decision')
        const questionnaireHmInfoMap = _.get(this.hmInterviewQuestionnaireResults, 'hire_manager_map')
        _.forEach(this.groupQuestionnaireResults(this.hmInterviewQuestionnaireResults, questionnaireOverallScoreMap), interviewResult => {
          const evaluation = this.normalizeQuestionnaireResultsEvaluation(interviewResult)
          const interviewSchedule = _.find(interviewResult.result_list, record => record.interview_time && record.interview_end_time)
          list.push({
            id: `new-interview-overall-rating-${interviewResult.uuid}`,
            type: INTERVIEW_OVERALL_RATING_TYPE.NEW,
            score: this.formatNullableScore(interviewResult.overall_total_score),
            scoreTotal: evaluation.hr_score_total,
            decision: _.get(questionnaireOverallDecisionMap, [interviewResult.uuid], INTERVIEW_RATING_DECISION.NOT_RATE_YET),
            completion: _.get(questionnaireOverallCompletionMap, [interviewResult.uuid], null),
            hmInfoList: _.get(questionnaireHmInfoMap, [interviewResult.uuid], []),
            interviewTime: interviewSchedule && interviewSchedule.interview_time,
            interviewEndTime: interviewSchedule && interviewSchedule.interview_end_time,
            evaluation
          })
        })
      }
      if (this.hasLegacyInterviewOverallRating) {
        const latestInterview = this.getLatestLegacyInterview()
        list.push({
          id: 'legacy-interview-overall-rating',
          type: INTERVIEW_OVERALL_RATING_TYPE.LEGACY,
          score: this.formatNullableScore(_.meanBy(this.legacyInterviewRateList, item => Number(item.total_rate))),
          scoreTotal: 100,
          decision: this.getInterviewOverallRatingRecommendation(_.map(this.legacyInterviewRateList, item => RATE_STATUS_MAP[item.status])),
          completion: 100,
          hmInfoList: _.map(this.legacyInterviewRateList, item => {
            return {
              id: item.id,
              full_name: item.hm_name,
              score: this.formatNullableScore(item.total_rate),
              interview_decision: this.getInterviewOverallRatingRecommendation([RATE_STATUS_MAP[item.status]])
            }
          }),
          interviewTime: latestInterview && latestInterview.interview_time,
          interviewEndTime: latestInterview && latestInterview.interview_end_time,
          ratingList: this.legacyInterviewRateList
        })
      }
      return _.chain(list)
        .sortBy([
          item => item.type === INTERVIEW_OVERALL_RATING_TYPE.LEGACY ? 0 : 1,
          item => item.interviewTime ? moment(item.interviewTime).valueOf() : Infinity
        ])
        .map((item, index) => ({
          ...item,
          label: this.getInterviewOverallRatingLabel(index, list.length)
        }))
        .value()
    },
    activeInterviewOverallRating () {
      return this.interviewOverallRatingList[this.activeInterviewOverallRatingIndex] || this.interviewOverallRatingList[0]
    },
    activeInterviewOverallRatingTime () {
      const rating = this.activeInterviewOverallRating
      if (!rating) {
        return '-'
      }
      if (!rating.interviewTime || !rating.interviewEndTime) {
        return '-'
      }
      const isEnglish = isEn()
      const interviewTime = moment(rating.interviewTime).locale(isEnglish ? 'en' : 'zh-cn')
      const interviewEndTime = moment(rating.interviewEndTime)
      return `${interviewTime.format(isEnglish ? 'dddd, MMM Do, YYYY HH:mm' : 'dddd, YYYY年MM月DD日, HH:mm')}-${interviewEndTime.format('HH:mm')}`
    },
    candidateId () {
      return this.candidateBaseInfo.id
    },
    jobId () {
      return this.candidateBaseInfo.job.id
    },
    hasInterviewPermission () {
      return this.hasAnyPermission('PERM_HR_INTERVIEW_SCHEDULING')
    },
    hasAIInterviewPermission () {
      return this.hasAnyPermission('PREMIUM_AI_INTERVIEW')
    },
    showAIInterviewReminderBtn () {
      return Boolean(this.aiInterviewRecord) && !_.includes([AI_INTERVIEW_STATUS.FINISHED, AI_INTERVIEW_STATUS.CANCELED], this.aiInterviewRecord.status)
    },
    btnList () {
      const vm = this
      if (this.candidateBaseInfo.is_agency_uploaded_and_hid || !this.hasInterviewPermission) {
        return []
      }
      const list = [
        {
          label: this.$t(`${this.i18nPrefix}.btn.hiringManagerInterview`),
          onclick: this.onInviteInterviewBtnClick
        },
        {
          label: this.$t(`${this.i18nPrefix}.btn.startInterview`),
          onclick: this.onStartInterviewBtnClick
        },
        {
          hidden: !this.hasAIInterviewPermission || this.aiInterviewRecord?.total_score,
          isDisabled: Boolean(this.aiInterviewRecord) && !this.aiInterviewRecord?.total_score,
          get label () {
            if (vm.aiInterviewRecord) {
              return vm.$t(`${vm.i18nPrefix}.btn.hasInviteAIInterview`, { date: moment(vm.aiInterviewRecord.created_time).format('YYYY/MM/DD') })
            } else {
              return vm.$t(`${vm.i18nPrefix}.btn.inviteAIInterview`)
            }
          },
          onclick: this.onInviteAIInterviewBtnClick
        },
        {
          label: this.$t(`${this.i18nPrefix}.btn.sendAIInterviewReminder`),
          hidden: !this.hasAIInterviewPermission || !this.showAIInterviewReminderBtn || this.candidateBaseInfo?.company?.root_company_id !== ROOT_COMPANY_ID.MERCEDES_BENZ,
          loading: this.aiInterviewReminderLoading,
          onclick: this.onSendAIInterviewReminderBtnClick
        }
      ]
      _.forEach(list, item => {
        Object.defineProperty(item, 'disabled', {
          enumerable: true,
          configurable: true,
          get () {
            return vm.isDisabled || item.isDisabled
          }
        })
        Object.defineProperty(item, 'tooltip', {
          enumerable: true,
          configurable: true,
          get () {
            return vm.isDisabled && vm.$t(`${vm.i18nPrefix}.message.disabledTip`)
          }
        })
      })
      return list
    },
    ratingQuestions () {
      return _.map(this.candidateBaseInfo.interview_questions, question => {
        return {
          title: question.title,
          hm_rate: _.map(question.rate, rate => {
            return {
              invite_record: rate.interview_record,
              comment: rate.comment,
              status: rate.status,
              rate: rate.rate
            }
          }),
          hm_rate_avg: this.formatStringFloorNumber(`${question.rate_avg}`)
        }
      })
    },
    applicationList () {
      return [{
        job: {
          ...this.candidateBaseInfo.job,
          company: this.candidateBaseInfo.company
        },
        candidate: {
          id: this.candidateId,
          name: this.candidateBaseInfo.other_name,
          ai_summary_info: this.candidateBaseInfo.ai_summary_info
        },
        statusName: this.candidateBaseInfo.current_status.i18nName
      }]
    },
    isEditingGroupInterview () {
      return this.inviteDialog.mode === INVITE_DIALOG_MODE.EDIT && _.get(this.inviteDialog.originInvitation, 'is_group_interview') === true
    }
  },
  created () {
    this.loadInterviewList()
    this.loadAIInterviewRecord()
    this.loadHrInterviewQuestionnaireResults()
  },
  watch: {
    interviewOverallRatingList (list) {
      if (this.activeInterviewOverallRatingIndex > list.length - 1) {
        this.activeInterviewOverallRatingIndex = 0
      }
    }
  },
  methods: {
    ...mapActions('common', ['getPrivateFileUrl']),
    ...mapActions('application', ['createHrInterview']),
    ...mapActions('interview', [
      'searchInterviewRecordList',
      'editInvitation',
      'resendInvitation',
      'resendInvitationRatingLink',
      'getJobInterviewRoomInfo',
      'inviteAIInterview',
      'remindAIInterview',
      'getAIInterviewRecordInfo',
      'getHrInterviewQuestionnaireResults',
      'updateInterviewRecordInfo'
    ]),
    ...mapActions('microsoftGraph', ['checkMicrosoftGraphAuth', 'deleteOnlineMeetingEvent']),
    formatStringFloorNumber (str) {
      return _.round(parseFloat(str), 1)
    },
    getInterviewOverallRatingLabel (index, total) {
      if (total === 1) {
        return this.$t(`${this.i18nPrefix}.label.interview`)
      }
      return this.$t(`${this.i18nPrefix}.label.interviewSequence`, { number: index + 1 })
    },
    getLatestLegacyInterview () {
      const scheduledRatings = _.filter(this.legacyInterviewRateList, item => item.interview_time)
      return _.maxBy(scheduledRatings, item => moment(item.interview_time).valueOf())
    },
    getInterviewOverallRatingRecommendation (recommendations) {
      const recommendationList = _.uniq(_.compact(recommendations))
      if (!recommendationList.length) {
        return INTERVIEW_RATING_DECISION.NOT_RATE_YET
      }
      if (recommendationList.length === 1) {
        return recommendationList[0]
      }
      const hasRecommended = recommendationList.includes(INTERVIEW_RATING_DECISION.RECOMMENDED)
      const hasNotRecommended = recommendationList.includes(INTERVIEW_RATING_DECISION.NOT_RECOMMENDED)
      if (hasRecommended && hasNotRecommended) {
        return INTERVIEW_RATING_DECISION.CONFLICT
      }
      if (hasRecommended) {
        return INTERVIEW_RATING_DECISION.RECOMMENDED
      }
      if (hasNotRecommended) {
        return INTERVIEW_RATING_DECISION.NOT_RECOMMENDED
      }
      return INTERVIEW_RATING_DECISION.PENDING
    },
    toggleInterviewOverallRating () {
      this.interviewOverallRatingCollapsed = !this.interviewOverallRatingCollapsed
    },
    handleInterviewOverallRatingTabClick (index) {
      this.activeInterviewOverallRatingIndex = index
    },
    async loadHrInterviewQuestionnaireResults () {
      if (!this.jobId || !this.candidateId) {
        return
      }
      this.interviewResultLoading = true
      try {
        const resp = await this.getHrInterviewQuestionnaireResults({
          job_id: this.jobId,
          candidate_id: this.candidateId
        })
        this.hmInterviewQuestionnaireResults = resp || null
      } catch (e) {
        this.hmInterviewQuestionnaireResults = null
      } finally {
        this.interviewResultLoading = false
      }
    },
    /**
     * 生成HM评分详情
     * @param {Array} scores - 评分列表
     * @param {Array} hireManagers - 招聘经理列表
     * @returns {Array} HM评分详情
     */
    generateHmResultDetails (scores, hireManagers) {
      return _.chain(scores)
        .filter(score => score.hm_id)
        .map(score => {
          const hm = _.find(hireManagers, { id: score.hm_id })
          return {
            hmName: hm.full_name,
            decision: score.submit_time ? score.decision : null,
            submitTime: score.submit_time
          }
        })
        .value()
    },
    getInvitationApplicationList (record) {
      if (!record.is_group_interview) {
        return this.applicationList
      }
      const job = {
        ...this.candidateBaseInfo.job,
        company: this.candidateBaseInfo.company
      }
      return _.chain(record.candidate)
        .uniqBy('id')
        .map(candidate => ({ candidate, job }))
        .value()
    },
    onInviteInterviewBtnClick () {
      this.inviteDialog.applicationList = this.applicationList
      this.inviteDialog.saveWithEvent = false
      this.inviteDialog.mode = INVITE_DIALOG_MODE.CREATE
      this.inviteDialog.originInvitation = null
      this.inviteDialog.invitation = null
      this.$refs.inviteDialog.showDialog()
    },
    async loadInterviewList () {
      this.interviewListLoading = true
      try {
        const resp = await this.searchInterviewRecordList({
          job_id: this.jobId,
          candidate_id: this.candidateId
        })
        if (resp) {
          const vm = this
          this.interviewList = _.map(resp, interview => {
            const hasMultipleCandidates = interview.candidate && interview.candidate.length > 1

            // 为顶级行也添加hmResultDetails（仅在单候选人场景下用于popover展示）
            const topHmResultDetails = !hasMultipleCandidates ? this.generateHmResultDetails(interview.interview_scores, interview.hire_manager) : []

            const needCalculateInterviewStatus = [INTERVIEW_STATUS.FINISHED, INTERVIEW_STATUS.PARTIALLY_RATED].includes(interview.status)

            let hireManager = interview.hire_manager
            if (interview.status === INTERVIEW_STATUS.PARTIALLY_RATED) {
              const scoresByHm = _.groupBy(interview.interview_scores, 'hm_id')
              hireManager = _.map(interview.hire_manager, hm => {
                const hmScores = scoresByHm[hm.id]
                const hasDecision = _.filter(hmScores, 'submit_time')
                const recordStatus = hasDecision.length === hmScores.length
                  ? INTERVIEW_STATUS.FINISHED
                  : (hasDecision.length === 0 ? INTERVIEW_STATUS.PENDING : INTERVIEW_STATUS.PARTIALLY_RATED)
                return {
                  ...hm,
                  record_status: recordStatus
                }
              })
            }
            return {
              ...interview,
              hmResultDetails: topHmResultDetails,
              hire_manager: hireManager,
              get interviewDate () {
                if (!interview.interview_time) {
                  return '-'
                }
                const i18nTime = moment(interview.interview_time).locale(vm.$i18n.locale)
                const year = i18nTime.format('YYYY')
                const month = i18nTime.format('MMM')
                const weekday = i18nTime.format('dddd')
                const day = i18nTime.format('Do')
                return vm.$t(`${vm.i18nPrefix}.label.interviewDay`, { year, month, weekday, day })
              },
              get interviewTime () {
                if (!interview.interview_time) {
                  return '-'
                }
                const startTime = moment(interview.interview_time).format('HH:mm')
                const endTime = moment(interview.interview_end_time).format('HH:mm')
                return vm.$t(`${vm.i18nPrefix}.label.interviewTime`, { startTime, endTime })
              },
              get recruiter () {
                return interview.creator.full_name
              },
              get hiringManagerNames () {
                return _.chain(interview.hire_manager).map('full_name').join(', ').value()
              },
              get candidateNames () {
                return interview.is_group_interview
                  ? interview.group_name
                  : _.chain(interview.candidate).map('name').join(', ').value()
              },
              get children () {
                if (hasMultipleCandidates || interview.is_group_interview) {
                  return _.map(interview.candidate, candidate => {
                    // 获取该候选人的所有HM评分记录
                    const candidateScores = _.filter(interview.interview_scores, { candidate_id: candidate.id })

                    // 计算该候选人的面试结果
                    const submittedScores = candidateScores.filter(score => score.submit_time)
                    const hasScore = submittedScores.length > 0

                    let candidateInterviewStatus = interview.status
                    if (needCalculateInterviewStatus) {
                      if (hasScore) {
                        if (submittedScores.length === interview.hire_manager.length) {
                          candidateInterviewStatus = INTERVIEW_STATUS.FINISHED
                        } else {
                          candidateInterviewStatus = INTERVIEW_STATUS.PARTIALLY_RATED
                        }
                      } else {
                        candidateInterviewStatus = INTERVIEW_STATUS.PENDING
                      }
                    }

                    // 生成该候选人的HM评分详情
                    const candidateHmResultDetails = vm.generateHmResultDetails(candidateScores, interview.hire_manager)
                    const hireManager = _.map(interview.hire_manager, hm => {
                      const hmRecord = _.find(candidateScores, { hm_id: hm.id })
                      return {
                        ...hm,
                        record_status: hmRecord?.record_status
                      }
                    })
                    return {
                      ...interview,
                      uuid: `${interview.uuid}_${candidate.id}`,
                      is_group_interview: false,
                      candidate: [candidate],
                      candidateId: candidate.id,
                      recruiter: interview.creator.full_name,
                      hire_manager: hireManager,
                      hiringManagerNames: _.chain(interview.hire_manager).map('full_name').join(', ').value(),
                      candidateNames: candidate.name,
                      status: candidateInterviewStatus,
                      interview_decision: interview.candidate_interview_decision?.[candidate.id],
                      hmResultDetails: candidateHmResultDetails,
                      hiddenAction: true
                    }
                  })
                }
                return null
              }
            }
          })
          this.searchInterviewRecords()
          const uuid = this.$route.query.uuid
          if (!uuid) {
            return
          }
          const record = _.find(this.interviewList, { uuid })
          if (record.status !== INTERVIEW_STATUS.PENDING_GENERATE_TEAMS_URL && record.status !== INTERVIEW_STATUS.PENDING_GENERATE_TEAMS_EVENT) {
            return
          }
          this.handleConfirmInterview(record)
        }
      } finally {
        this.interviewListLoading = false
      }
    },
    async loadAIInterviewRecord () {
      if (!this.hasAIInterviewPermission) {
        return
      }
      try {
        const params = {
          job_id: this.jobId,
          candidate_id: this.candidateId
        }
        const resp = await this.getAIInterviewRecordInfo(params)
        if (resp) {
          resp.finished_time = resp.finished_time ? moment(resp.finished_time).format('YYYY-MM-DD HH:mm:ss') : null
          this.aiInterviewRecord = resp
        }
      } finally {

      }
    },
    searchInterviewRecords (search) {
      search = search || {}
      this.interviewListLoading = true
      const keywords = (search.keywords || '').toUpperCase()
      this.searchResult = _.filter(this.interviewList, (record) => {
        let hasSearched = true
        if (search.status?.length > 0) {
          hasSearched = _.includes(search.status, record.status)
        }
        if (hasSearched && keywords) {
          hasSearched = record.recruiter.toUpperCase().includes(keywords) ||
                        _.some(record.hire_manager, (hm) => hm.full_name.toUpperCase().includes(keywords)) ||
                        _.some(record.candidate, (ca) => ca.name.toUpperCase().includes(keywords))
        }
        return hasSearched
      })
      this.interviewListLoading = false
    },
    handleViewGroupInterview (record) {
      window.open(`/recruiter-pc/v-admin/application/group-interview?uuid=${encodeURIComponent(record.uuid)}`)
    },
    async onStartInterviewBtnClick () {
      this.selectedQuestionnaire = null
      await this.loadDefaultInterviewQuestionnaire()
      this.showQuestionnaireSelector = true
    },
    async loadDefaultInterviewQuestionnaire () {
      const defaultQuestionnaireId = _.get(this.candidateBaseInfo, 'job.default_interview_questionnaire_id')
      if (!defaultQuestionnaireId) {
        return
      }
      try {
        this.selectedQuestionnaire = await this.$refs.questionnaireSelectorDialog.loadQuestionnaireById(defaultQuestionnaireId)
      } catch (e) {
        this.selectedQuestionnaire = null
      }
    },
    async handleConfirmQuestionnaire (questionnaire) {
      this.selectedQuestionnaire = questionnaire
      this.$eventBus.$emit('showLoading', true)
      try {
        const resp = await this.createHrInterview({
          job_id: this.jobId,
          candidate_ids: [this.candidateId],
          questionnaire_id: questionnaire?.id
        })
        if (resp) {
          window.open(`/recruiter-pc/v-admin/hm/candidate/profile/interview?candidate_id=${this.candidateId}&job_id=${this.jobId}&uuid=${resp.uuid}`)
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async loadJobInterviewRoomInfo () {
      if (!this.jobId) {
        return
      }
      const resp = await this.getJobInterviewRoomInfo(this.jobId)
      if (resp) {
        return resp.interview_room_id
      }
    },
    handleCancelCreateJobAIInterviewRoom () {
      this.createJobInterviewRoomConfirmDialog.show = false
    },
    handleJobAIInterviewRoomTabMessage (event) {
      if (event.origin !== window.location.origin) {
        return
      }
      const data = event.data
      if (data?.hasClosed) {
        this.onInviteAIInterviewBtnClick()
      }
    },
    handleConfirmCreateJobAIInterviewRoom () {
      window.open(`/v-admin/job/edit/${this.jobId}?step=AI_INTERVIEW_ROOM&close_tab=1`)
      if (window.addEventListener) {
        window.addEventListener('message', this.handleJobAIInterviewRoomTabMessage, false)
      } else {
        window.attachEvent('onmessage', this.handleJobAIInterviewRoomTabMessage)
      }
      this.createJobInterviewRoomConfirmDialog.show = false
    },
    async onInviteAIInterviewBtnClick () {
      this.$eventBus.$emit('showLoading', true)
      const interviewRoomId = await this.loadJobInterviewRoomInfo()
      // 没配置时弹窗提示
      if (!interviewRoomId) {
        this.$eventBus.$emit('showLoading', false)
        this.createJobInterviewRoomConfirmDialog.show = true
        return
      }
      try {
        const params = {
          company_id: this.candidateBaseInfo.company.id,
          job_id: this.jobId,
          candidate_ids: [this.candidateId]
        }
        const resp = await this.inviteAIInterview(params)
        if (resp) {
          this.$message({
            type: 'success',
            message: this.$t(`${this.i18nPrefix}.message.inviteAIInterviewSuccess`)
          })
          this.candidateBaseInfo.current_status = addI18nProperty(resp.candidate_status, 'i18nName', 'name_cn', 'name_en')
          this.loadAIInterviewRecord()
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async onSendAIInterviewReminderBtnClick () {
      if (this.aiInterviewReminderLoading) {
        return
      }
      this.aiInterviewReminderLoading = true
      try {
        const params = {
          company_id: this.candidateBaseInfo.company.id,
          job_id: this.jobId,
          candidate_id: this.candidateId
        }
        const resp = await this.remindAIInterview(params)
        if (resp) {
          this.$message({
            type: 'success',
            message: this.$t(`${this.i18nPrefix}.message.sendAIInterviewReminderSuccess`)
          })
        }
      } finally {
        this.aiInterviewReminderLoading = false
      }
    },
    handleSentInterviewInvite (inviteResult) {
      this.candidateBaseInfo.current_status = addI18nProperty(inviteResult.candidate_status, 'i18nName', 'name_cn', 'name_en')
      this.interviewListLoading = true
      this.$nextTick(() => {
        setTimeout(async () => {
          this.$emit('refreshProfileTab')
          this.loadInterviewList()
          await this.loadHrInterviewQuestionnaireResults()
          this.activeInterviewOverallRatingIndex = Math.max(this.interviewOverallRatingList.length - 1, 0)
        }, 500)
      })
    },
    async handleSaveInterviewInvite (invitation, callback) {
      let success = false
      try {
        const candidates = this.isEditingGroupInterview
          ? _.chain(this.inviteDialog.originInvitation.candidate).map('id').uniq().value()
          : invitation.candidates
        const resp = await this.editInvitation({
          ...invitation,
          candidates,
          is_edit: this.inviteDialog.mode === INVITE_DIALOG_MODE.EDIT,
          uid: this.inviteDialog.originInvitation.uuid,
          original_hm_ids: _.map(this.inviteDialog.originInvitation.hire_manager, 'id'),
          has_other_candidate: Object.keys(_.groupBy(this.inviteDialog.originInvitation.candidate, 'id')).length > 1
        })
        if (resp) {
          success = resp.success
          const action = this.$t('common.btn.invite')
          if (success) {
            this.candidateBaseInfo.current_status = addI18nProperty(resp.candidate_status, 'i18nName', 'name_cn', 'name_en')
            this.$message.success(this.$t('common.message.actionSuccess', { action }))
            this.interviewListLoading = true
            this.$nextTick(() => {
              setTimeout(async () => {
                this.loadInterviewList()
                await this.loadHrInterviewQuestionnaireResults()
                this.activeInterviewOverallRatingIndex = Math.max(this.interviewOverallRatingList.length - 1, 0)
              }, 500)
            })
          } else {
            this.$message.error(this.$t('common.message.actionFail', { action }))
          }
        }
      } finally {
        callback(success)
      }
    },
    async handleEditInterview (record) {
      this.inviteDialog.applicationList = this.getInvitationApplicationList(record)
      this.inviteDialog.saveWithEvent = true
      this.inviteDialog.mode = INVITE_DIALOG_MODE.EDIT
      this.inviteDialog.originInvitation = record
      this.inviteDialog.invitation = record
      this.$refs.inviteDialog.showDialog(record)
    },
    async handleResendInterview (record) {
      this.inviteDialog.applicationList = this.getInvitationApplicationList(record)
      this.inviteDialog.saveWithEvent = true
      this.inviteDialog.mode = INVITE_DIALOG_MODE.RESEND
      this.inviteDialog.originInvitation = record
      this.inviteDialog.invitation = record
      this.$refs.inviteDialog.showDialog(record)
    },
    async checkCurrentAccountAuthGraph (callback) {
      if (this.microsoftGraphAuthWin && this.microsoftGraphAuthWin.closed) {
        this.microsoftGraphAuthWin = null
        this.microsoftGraphAuthUrl = null
        return
      }
      const resp = await this.checkMicrosoftGraphAuth()
      if (resp) {
        this.hasMicrosoftGraphAuthToken = false
        if (!this.microsoftGraphAuthUrl) {
          this.microsoftGraphAuthUrl = resp
          this.microsoftGraphAuthWin = openNewBrowserWindowByUrl(this.microsoftGraphAuthUrl, null, 500, 600)
        }
        setTimeout(() => this.checkCurrentAccountAuthGraph(callback), 1000)
      } else {
        this.microsoftGraphAuthUrl = null
        this.hasMicrosoftGraphAuthToken = true
        if (this.microsoftGraphAuthWin) {
          this.microsoftGraphAuthWin.close()
        }
        callback()
      }
    },
    async cancelInterview (record) {
      this.$eventBus.$emit('showLoading', true)
      try {
        const resp = await this.updateInterviewRecordInfo({
          interviewUuid: record.uuid,
          data: {
            operation_type: INTERVIEW_OPERATION_TYPE.CANCEL
          }
        })
        if (resp) {
          const action = this.$t('common.btn.cancel')
          if (resp === 'success') {
            if (this.microsoftGraphEventId) {
              await this.deleteOnlineMeetingEvent({ eventId: this.microsoftGraphEventId })
            }
            record.status = INTERVIEW_STATUS.CANCELED_BY_ORGANIZER
            this.$message.success(this.$t('common.message.actionSuccess', { action }))
          } else if (resp === 'cannot_cancel_interview_due_to_deadline') {
            this.$message.warning(this.$t(`${this.i18nPrefix}.message.cannotCancelInterviewDueToDeadline`))
          } else if (resp === 'cannot_cancel_interview_rated') {
            this.$message.warning(this.$t(`${this.i18nPrefix}.message.cannotCancelInterviewHasBeenRated`))
          } else {
            this.$message.error(this.$t('common.message.actionFail', { action }))
          }
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    async handleCancelInterview (record) {
      this.microsoftGraphEventId = record.microsoft_graph_event_id
      if (this.microsoftGraphEventId) {
        await this.checkCurrentAccountAuthGraph(() => this.cancelInterview(record))
        if (!this.hasMicrosoftGraphAuthToken) {
          this.$message({
            type: 'warning',
            message: this.$t(`${this.i18nPrefix}.message.cannotCancelMicrosoftMeeting`)
          })
        }
      } else {
        await this.cancelInterview(record)
      }
    },
    async handleSendNewInterview (record) {
      this.inviteDialog.applicationList = this.applicationList
      this.inviteDialog.saveWithEvent = false
      this.inviteDialog.mode = INVITE_DIALOG_MODE.CREATE
      this.inviteDialog.originInvitation = null
      this.inviteDialog.invitation = null
      this.$refs.inviteDialog.showDialog()
    },
    async handleSendRatingInterview (record) {
      this.$eventBus.$emit('showLoading', true)
      try {
        const resp = await this.resendInvitationRatingLink({
          uid: record.uuid,
          job_id: record.job_id
        })
        if (resp) {
          const action = this.$t('common.btn.send')
          if (resp === 'success') {
            this.$message.success(this.$t('common.message.actionSuccess', { action }))
          } else {
            this.$message.error(this.$t('common.message.actionFail', { action }))
          }
        }
      } finally {
        this.$eventBus.$emit('showLoading', false)
      }
    },
    handleConfirmInterview (record) {
      this.$refs.interviewConfirmDialog.showDialog(record)
    },
    handleTeamsUrlCreateSuccess () {
      this.$nextTick(() => {
        this.loadInterviewList()
      })
    },
    async downloadReport (reportUrl) {
      if (!reportUrl) {
        return
      }
      if (_.startsWith(reportUrl, 'https://')) {
        window.open(reportUrl)
      } else if (_.startsWith(reportUrl, '/v2/ai-interview/candidate/')) {
        if (process.env.VUE_APP_ENV === 'development') {
          window.open('http://localhost:8088' + reportUrl)
        } else {
          window.open(reportUrl)
        }
      } else {
        const pdfUrl = await this.getPrivateFileUrl({ fileType: 'COMMON_FILE', fileKey: reportUrl })
        // pdf文件名: 公司名称_候选人名称_候选人ID_20230920.pdf
        const baseInfo = this.candidateBaseInfo
        const companyName = baseInfo.company.i18nName
        const candidateName = baseInfo.other_name
        const candidateId = baseInfo.id
        const fileName = `${companyName}_${candidateName}_${candidateId}_${moment().format('YYYYMMDD')}.pdf`
        const url = addQueryInPath(pdfUrl, {
          attname: fileName
        })
        window.location.href = url
      }
    },
    onPlayerContainerClick (question) {
      if (question.answer) {
        this.openPlayerDialog(question)
      }
    },
    async openPlayerDialog (question) {
      const questionType = question.question_type
      let type = 'video'
      if (questionType === AI_INTERVIEW_QUESTION_TYPE.AUDIO) {
        type = 'audio'
      }
      const popInfo = this.popInfo
      popInfo.title = question.title
      popInfo.type = type
      const url = question.answer
      if (_.startsWith(url, 'https://')) {
        popInfo.url = url
      } else {
        popInfo.url = await this.getPrivateFileUrl({ fileType: 'COMMON_FILE', fileKey: url })
      }
      popInfo.show = true
    },
    closePlayerDialog () {
      this.popInfo.show = false
      this.popInfo.title = null
      this.popInfo.url = null
    }
  }
}
</script>

<style lang="scss" scoped>
.interview-info-container {
  .interview-content {
    margin: 0 auto;

    .el-collapse {
      border: 0;
    }

    .interview-overall-rating {
      padding-bottom: 20px;

      .overall-rating-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 32px;
        width: 100%;

        .overall-rating-left {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 10px;
          min-width: 0;

          .overall-rating-title-row {
            display: flex;
            align-items: center;
            gap: 16px;
            padding: 0;
            border: 0;
            color: #333;
            background: transparent;
            cursor: pointer;

            .overall-rating-title {
              font-size: 18px;
              line-height: 48px;
              font-weight: bold;
              white-space: nowrap;
            }

            .overall-rating-arrow {
              display: flex;
              align-items: center;
              justify-content: center;
              width: 20px;
              font-size: 18px;
              transition: transform .3s;
              transform: rotate(-180deg);

              &.is-collapsed {
                transform: rotate(0deg);
              }
            }
          }

          .overall-rating-control {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 6px;
            min-width: 0;

            .overall-rating-tabs {
              position: relative;
              display: flex;
              align-items: center;
              min-height: 34px;

              .overall-rating-tab {
                display: flex;
                align-items: center;
                height: 20px;
                padding: 0 14px;
                border: 0;
                border-right: 1px solid #DCDFE6;
                color: #606266;
                background: transparent;
                cursor: pointer;
                font-size: 14px;
                line-height: 20px;
                white-space: nowrap;

                &:first-child {
                  padding-left: 0;
                }

                &:last-child {
                  border-right: 0;
                }

                &.is-active {
                  color: #0096CC;
                  font-weight: 600;
                }

                .overall-rating-tab-score {
                  margin-left: 6px;
                  width: 32px;
                  height: 32px;
                  border-radius: 50%;
                  background-color: #fff;
                  box-sizing: border-box;

                  .overall-rating-tab-score-value {
                    display: inline-block;
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                  }
                }
              }
            }

            .overall-rating-time {
              color: #606266;
              font-size: 14px;
              line-height: 14px;
              white-space: nowrap;
            }
          }
        }

        .overall-rating-score {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          flex-shrink: 0;

          .overall-score-circle {
            width: 70px;
            height: 70px;
            border-radius: 50%;
            background-color: #fff;
          }

          .overall-score-label {
            color: #909399;
            font-size: 14px;
            line-height: 14px;
          }
        }
      }

      &.is-collapsed {
        padding-bottom: 0;

        .overall-rating-header {
          justify-content: flex-start;
          gap: 0;
        }

        .overall-rating-left {
          gap: 0;
        }
      }

      .overall-rating-detail {
        margin-top: 20px;

        ::v-deep .ratings-container {
          margin-top: 0;
          margin-right: 0;
        }
      }
    }

    .ai-interview-form {
      .info-item {
        .ai-score {
          color: #606266;
        }

        .question-info {
          .line {
            & + .line {
              margin-top: 20px;
            }

            .label {
              margin-right: 4px;
              color: #303133;
              font-weight: bold;
            }

            .value {
              color: #606266;
              white-space: pre-wrap;
              flex: 1;

              &.link {
                color: #0096cc;
                text-decoration: underline;
                cursor: pointer;
              }
            }
          }

          .question {
            display: flex;
            align-items: flex-start;
          }

          .question-answer {
            display: flex;
            align-items: flex-start;
            margin-top: 2px;

            .label {
              color: #303133;
              font-weight: bold;
              margin-right: 4px;
            }

            .answer {
              flex: 1;
              color: #606266;
            }

            .en-answer {
              margin-right: 22px;
            }

            .video {
              .video-container {
                flex: initial;
                position: relative;
                width: 164px;
                height: 78px;
                cursor: pointer;
                background-image: url($baseDomain + 'static/image/video/video_bg.png');
                background-size: 100% 100%;

                .video-play-icon {
                  position: absolute;
                  top: 50%;
                  left: 50%;
                  width: 40px;
                  height: 40px;
                  transform: translate(-50%, -50%);
                }
              }
            }
          }
        }
      }
    }

    ::v-deep {
      .interview-management-container {
        .interview-management-table {
          .el-table__body-wrapper {
            overflow-x: hidden;
          }
        }
      }
    }
  }

  .el-collapse-item {
    ::v-deep .el-collapse-item__wrap {
      position: relative;
      overflow: initial;
      border-bottom: 0;
    }

    ::v-deep .el-collapse-item__header {
      font-size: 18px;
      border-bottom: 0;

      .title {
        font-weight: bold;
      }

      .el-collapse-item__arrow {
        margin-left: 15px;
      }
    }
  }

  .confirm-create-job-interview-room {
    .dialog-footer {
      .footer-btn  {
        border-radius: 2px;
        padding: 10px 30px;
      }

      .cancel-btn {
        height: 40px;
      }

      .save-btn {
        background: #0096cc;
        border-color: #0096cc;
        color: #ffffff;

        .label {
          vertical-align: middle;
        }

        .btn-icon {
          margin-left: 4px;
          font-size: 18px;
          vertical-align: middle;
        }
      }
    }
  }

  .player-dialog {
    ::v-deep .el-dialog__body {
      height: 60vh;
    }

    .video-player-container {
      height: calc(100% - 50px);
      text-align: center;

      .video {
        width: 100%;
        height: 100%;
      }
    }

    .audio-player-container {
      height: calc(100% - 50px);
      text-align: center;

      .audio {
        width: 100%;
        height: 100%;
      }
    }
  }

  .interview-rating-form {
    ::v-deep {
      .question-item {
        .question-container {
          padding-right: 90px;

          .question {
            .label {
              width: initial;
            }
          }
        }

        .rating-container {
          .total-score {
            top: 8px;
          }
        }
      }
    }
  }
}
</style>
